/**
 * useCameraClusters Hook
 *
 * Manages marker clustering for cameras on the dispatch map using supercluster.
 * Groups nearby cameras into clusters at low zoom levels, with alert status tracking.
 */

import { useMemo } from 'react';
import Supercluster from 'supercluster';
import {
  CameraLocation,
  CameraPointProperties,
  CameraPoint,
  CameraClusterOrPoint,
  MapBounds,
} from './types';

/** Options for clustering behavior */
export interface UseCameraClustersOptions {
  /** Cluster radius in pixels (default: 60) */
  radius?: number;
  /** Stop clustering at this zoom level (default: 15) */
  maxZoom?: number;
  /** Minimum points to form a cluster (default: 3) */
  minPoints?: number;
}

/** Return value from useCameraClusters hook */
export interface UseCameraClustersResult {
  /** Array of clusters and individual points to render */
  clusters: CameraClusterOrPoint[];
  /** The supercluster instance for expansion zoom calculation */
  supercluster: Supercluster<CameraPointProperties, CameraPointProperties> | null;
  /** Get all camera points within a cluster */
  getClusterLeaves: (clusterId: number) => CameraPoint[];
}

/**
 * Hook for clustering camera markers on the map.
 *
 * @param cameras - Array of camera locations to cluster
 * @param bounds - Current map viewport bounds (null = show all as individual)
 * @param zoom - Current map zoom level
 * @param options - Clustering options
 * @returns Clusters/points to render and helper functions
 */
export function useCameraClusters(
  cameras: CameraLocation[],
  bounds: MapBounds | null,
  zoom: number,
  options?: UseCameraClustersOptions
): UseCameraClustersResult {
  const { radius = 60, maxZoom = 15, minPoints = 3 } = options ?? {};

  // Convert cameras to GeoJSON points with alert status
  const points: CameraPoint[] = useMemo(
    () =>
      cameras.map((camera) => ({
        type: 'Feature' as const,
        properties: {
          cameraId: camera.id,
          name: camera.display_name || camera.name,
          status: camera.status,
          // Alert = has any alerts
          hasAlerts: camera.alert_count > 0,
        },
        geometry: {
          type: 'Point' as const,
          coordinates: [camera.longitude, camera.latitude] as [number, number],
        },
      })),
    [cameras]
  );

  // Initialize supercluster with custom reduce for alert aggregation
  const supercluster = useMemo(() => {
    const sc = new Supercluster<CameraPointProperties, CameraPointProperties>({
      radius,
      maxZoom,
      minPoints,
      // Map function: transform point properties for clustering
      map: (props) => ({
        ...props,
        // Convert boolean to number for aggregation
        hasAlerts: props.hasAlerts ? 1 : 0,
      } as unknown as CameraPointProperties),
      // Reduce function: aggregate alert counts across cluster members
      reduce: (accumulated, props) => {
        // Sum up hasAlerts counts (treated as numbers during reduce)
        (accumulated as any).hasAlerts += (props as any).hasAlerts;
      },
    });
    sc.load(points);
    return sc;
  }, [points, radius, maxZoom, minPoints]);

  // Get clusters for current viewport
  const clusters = useMemo(() => {
    // No bounds = return all as individual points (initial load)
    if (!bounds) {
      return points;
    }

    // Convert bounds to bbox format: [westLng, southLat, eastLng, northLat]
    const bbox: [number, number, number, number] = [
      bounds.west,
      bounds.south,
      bounds.east,
      bounds.north,
    ];

    return supercluster.getClusters(bbox, Math.floor(zoom));
  }, [supercluster, bounds, zoom, points]);

  // Helper to get all camera points within a cluster
  const getClusterLeaves = (clusterId: number): CameraPoint[] => {
    return supercluster.getLeaves(clusterId, Infinity);
  };

  return { clusters, supercluster, getClusterLeaves };
}

export default useCameraClusters;
